// src/lib/darkModeOverrides.js
//
// CSS [style*=...] attribute selectors are unreliable for inline styles
// because browsers normalize color values inconsistently between properties
// (color may keep `#0F172A` while background-color gets serialized as
// `rgb(255, 255, 255)`, etc.). Rather than guess every possible
// serialization form, walk the DOM directly and override matching inline
// styles via setProperty(..., 'important').
//
// Runs only when dark mode is active. A MutationObserver keeps up with
// React re-renders that reset the inline value back to the light-mode
// color — we just re-override on the next tick. Originals are saved per
// element on first override so we can restore cleanly when dark mode
// toggles off (without depending on React to re-render every component).

// Lowercased keys → dark replacements. Both hex AND rgb() forms listed
// because either may show up depending on the property and browser.
const BG_MAP = {
  'white': '#161b26',
  '#fff': '#161b26',
  '#ffffff': '#161b26',
  'rgb(255, 255, 255)': '#161b26',
  'rgb(255,255,255)': '#161b26',

  '#fafbfd': '#1c2230',
  'rgb(250, 251, 253)': '#1c2230',
  'rgb(250,251,253)': '#1c2230',

  '#fafbfc': '#1c2230',

  '#f1f5f9': '#1e2532',
  'rgb(241, 245, 249)': '#1e2532',
  'rgb(241,245,249)': '#1e2532',

  '#e5e9f0': '#252b38',
  'rgb(229, 233, 240)': '#252b38',
  'rgb(229,233,240)': '#252b38',

  '#f0f2f6': '#1e2430',
  'rgb(240, 242, 246)': '#1e2430',
  'rgb(240,242,246)': '#1e2430',

  '#eff4fb': 'rgba(56, 110, 188, 0.12)',
  'rgb(239, 244, 251)': 'rgba(56, 110, 188, 0.12)',
};

const COLOR_MAP = {
  '#0f172a': '#f1f5f9',
  'rgb(15, 23, 42)':  '#f1f5f9',
  'rgb(15,23,42)':    '#f1f5f9',

  '#334155': '#e2e8f0',
  'rgb(51, 65, 85)':  '#e2e8f0',
  'rgb(51,65,85)':    '#e2e8f0',

  '#475569': '#cbd5e1',
  'rgb(71, 85, 105)': '#cbd5e1',
  'rgb(71,85,105)':   '#cbd5e1',

  '#64748b': '#94a3b8',
  'rgb(100, 116, 139)': '#94a3b8',
  'rgb(100,116,139)':   '#94a3b8',

  // NOTE: '#cbd5e1' used to map back to '#475569' here — a direct cycle
  // with the '#475569' entry above (A→B and B→A both present). Once
  // anything triggered the first conversion, the MutationObserver would
  // see its own resulting style mutation, re-run applyTo, see the new
  // value matches a COLOR_MAP key, flip it back, observe THAT mutation,
  // flip it again — forever, at animation-frame speed. Confirmed as the
  // cause of a continuous flicker on Market Share's county table, where
  // '#475569' (most cells) and '#cbd5e1' (empty-state placeholders) sit
  // in the same table. '#cbd5e1' is light enough to stay readable as-is
  // on a dark surface, so it doesn't need a mapping at all — removed
  // instead of "fixed" to point elsewhere, since any A→B with a B→A
  // anywhere else in this map (or BG_MAP/BORDER_COLOR_MAP) reproduces the
  // exact same failure mode. The dataset-guard added to applyTo() below
  // is the general-case defense; this removal fixes the specific case
  // that actually fired.

  '#1a3665': '#93b8e8',
  'rgb(26, 54, 101)': '#93b8e8',
  'rgb(26,54,101)':   '#93b8e8',

  '#6b7280': '#94a3b8',
  '#707578': '#94a3b8',
};

const BORDER_COLOR_MAP = {
  '#e5e9f0': '#252b38',
  'rgb(229, 233, 240)': '#252b38',
  'rgb(229,233,240)':   '#252b38',
  '#f0f2f6': '#1e2430',
  'rgb(240, 242, 246)': '#1e2430',
  'rgb(240,242,246)':   '#1e2430',
};

// Sidebar (aside) is styled separately — don't touch it from here.
function shouldSkip(el) {
  return el.closest('aside') !== null;
}

function normalize(v) {
  return (v || '').toLowerCase().trim().replace(/;$/, '');
}

// Translucent light backgrounds (sticky bars, popovers etc.) — match
// rgba(250, 251, 253, X) and rgba(255, 255, 255, X) and rewrite to a
// translucent dark equivalent.
function rewriteTranslucentLight(value) {
  if (!value) return null;
  const s = value.toLowerCase();
  let m = s.match(/rgba?\(\s*250\s*,?\s*251\s*,?\s*253\s*(?:,\s*([\d.]+))?\s*\)/);
  if (m) {
    const a = m[1] ?? '1';
    return `rgba(22, 27, 38, ${a})`;
  }
  m = s.match(/rgba?\(\s*255\s*,?\s*255\s*,?\s*255\s*(?:,\s*([\d.]+))?\s*\)/);
  if (m) {
    const a = m[1] ?? '1';
    return `rgba(22, 27, 38, ${a})`;
  }
  return null;
}

// Gradients on cards (e.g. `linear-gradient(180deg, #FFFFFF 0%, #FAFBFD 100%)`)
// → flatten to solid dark-card color. Brand gradients (navy / accent
// gradients used as feature highlights) are preserved.
function rewriteLightGradient(value) {
  if (!value) return null;
  const s = value.toLowerCase();
  if (!s.includes('linear-gradient') && !s.includes('radial-gradient')) return null;
  if (s.includes('#1a3665') || s.includes('#2e4b7c') || s.includes('rgb(26')) return null;
  if (s.includes('#8b5cf6') || s.includes('#a78bfa') || s.includes('#7c3aed') ||
      s.includes('#0891b2') || s.includes('#06b6d4') ||
      s.includes('#10b981') || s.includes('#059669') || s.includes('#34d399') ||
      s.includes('#dc2626') || s.includes('#ef4444') ||
      s.includes('#475569') || s.includes('#64748b')) return null;
  if (s.includes('#ffffff') || s.includes('#fff') || s.includes('white') ||
      s.includes('#fafbfd') || s.includes('#f1f5f9') || s.includes('#fafbfc') ||
      s.includes('rgb(255')) {
    return '#161b26';
  }
  return null;
}

// Save the original inline value (without priority) so we can restore on
// disable. Stored in dataset because that survives React re-renders even
// when React re-emits the inline style attribute.
//
// Each key is "dm" + camelCased property name. We also track the "priority"
// (empty or 'important') in case the original was already !important.
function saveOriginal(el, prop, datasetKey) {
  if (el.dataset[datasetKey] !== undefined) return; // already saved
  const value = el.style.getPropertyValue(prop);
  const priority = el.style.getPropertyPriority(prop);
  el.dataset[datasetKey] = value + (priority ? '!' + priority : '');
}

function override(el, cssProp, datasetKey, newValue) {
  saveOriginal(el, cssProp, datasetKey);
  el.style.setProperty(cssProp, newValue, 'important');
  // Record what the browser actually stored (not the literal newValue
  // string) — some browsers re-serialize hex to rgb() even for inline
  // styles set via JS, so comparing against our own literal would silently
  // never match and this guard would quietly no-op. Reading it straight
  // back through the same getPropertyValue()+normalize() path used below
  // guarantees the comparison is apples-to-apples.
  el.dataset[datasetKey + 'Val'] = normalize(el.style.getPropertyValue(cssProp));
}

// True if the property's current value is exactly what the walker itself
// wrote last time (tracked via override() above), meaning nothing has
// touched it since — skip re-examining it against the maps.
//
// This is NOT the same as "has this element/property ever been
// converted" (that blunter check breaks legitimate updates: a cell whose
// color depends on data — e.g. the share-percentage threshold colors —
// needs to keep converting to whatever NEW light value React writes on
// each re-render, per this file's original design). It specifically
// catches the case where the current value is unchanged since our own
// last write, which is what breaks a ping-pong: convert A→B, the
// MutationObserver sees that mutation and re-runs applyTo, reads B —
// unchanged since we wrote it — skip, instead of asking "is B also some
// OTHER entry's source key" and flipping it back to A. Confirmed cause of
// a continuous flicker on Market Share (see the removed '#475569' /
// '#cbd5e1' COLOR_MAP entries) — kept as defense against any other
// undiscovered A→B / B→A collision across BG_MAP/COLOR_MAP/BORDER_COLOR_MAP.
function wasJustApplied(el, cssProp, datasetKey) {
  const applied = el.dataset[datasetKey + 'Val'];
  return applied !== undefined && normalize(el.style.getPropertyValue(cssProp)) === applied;
}

function applyTo(el) {
  if (shouldSkip(el)) return;
  const style = el.style;
  if (!style) return;

  // background-color (solid)
  const bg = normalize(style.backgroundColor);
  if (bg && !wasJustApplied(el, 'background-color', 'dmBg')) {
    if (BG_MAP[bg] && style.backgroundColor !== BG_MAP[bg]) {
      override(el, 'background-color', 'dmBg', BG_MAP[bg]);
    } else {
      const translucent = rewriteTranslucentLight(bg);
      if (translucent && style.backgroundColor !== translucent) {
        override(el, 'background-color', 'dmBg', translucent);
      }
    }
  }

  // background-image (gradients via background shorthand land here)
  const bgImg = normalize(style.backgroundImage);
  if (bgImg && bgImg !== 'none' && !wasJustApplied(el, 'background-image', 'dmBgImg')) {
    const replaced = rewriteLightGradient(bgImg);
    if (replaced && style.backgroundImage !== 'none') {
      override(el, 'background-image', 'dmBgImg', 'none');
      override(el, 'background-color', 'dmBg', replaced);
    }
  }

  // color
  const c = normalize(style.color);
  if (c && !wasJustApplied(el, 'color', 'dmColor') && COLOR_MAP[c] && style.color !== COLOR_MAP[c]) {
    override(el, 'color', 'dmColor', COLOR_MAP[c]);
  }

  // Gradient-clipped text — invisible on dark bg, force solid light fill.
  const fill = normalize(style.webkitTextFillColor);
  if ((fill === 'transparent' || fill === 'rgba(0, 0, 0, 0)') && !wasJustApplied(el, '-webkit-text-fill-color', 'dmFill')) {
    override(el, '-webkit-text-fill-color', 'dmFill', '#f1f5f9');
    override(el, 'color', 'dmColor', '#f1f5f9');
    override(el, 'background-image', 'dmBgImg', 'none');
    override(el, 'background-color', 'dmBg', 'transparent');
  }

  // border-color (set explicitly OR via shorthand). Reading style.borderColor
  // returns the resolved value when all four sides match.
  const bc = normalize(style.borderColor);
  if (bc && !wasJustApplied(el, 'border-color', 'dmBorder') && BORDER_COLOR_MAP[bc] && style.borderColor !== BORDER_COLOR_MAP[bc]) {
    override(el, 'border-color', 'dmBorder', BORDER_COLOR_MAP[bc]);
  }
  // border shorthand puts the color into top/right/bottom/left individually
  const sides = [
    ['borderTopColor',    'border-top-color',    'dmBorderTop'],
    ['borderRightColor',  'border-right-color',  'dmBorderRight'],
    ['borderBottomColor', 'border-bottom-color', 'dmBorderBottom'],
    ['borderLeftColor',   'border-left-color',   'dmBorderLeft'],
  ];
  for (const [jsKey, cssProp, dsKey] of sides) {
    if (wasJustApplied(el, cssProp, dsKey)) continue;
    const v = normalize(style[jsKey]);
    if (v && BORDER_COLOR_MAP[v] && style[jsKey] !== BORDER_COLOR_MAP[v]) {
      override(el, cssProp, dsKey, BORDER_COLOR_MAP[v]);
    }
  }
}

export function applyAll(root) {
  const scope = root && root.querySelectorAll ? root : document.body;
  if (scope.matches && scope.matches('[style]')) applyTo(scope);
  const els = scope.querySelectorAll('[style]');
  for (const el of els) applyTo(el);
}

// Map of dataset key → CSS property (so restore knows what to put back).
const RESTORE_KEYS = [
  ['dmBg',           'background-color'],
  ['dmBgImg',        'background-image'],
  ['dmColor',        'color'],
  ['dmFill',         '-webkit-text-fill-color'],
  ['dmBorder',       'border-color'],
  ['dmBorderTop',    'border-top-color'],
  ['dmBorderRight',  'border-right-color'],
  ['dmBorderBottom', 'border-bottom-color'],
  ['dmBorderLeft',   'border-left-color'],
];

function restoreOne(el) {
  for (const [dsKey, cssProp] of RESTORE_KEYS) {
    const saved = el.dataset[dsKey];
    if (saved === undefined) continue;
    const bangAt = saved.lastIndexOf('!important');
    let value, priority;
    if (bangAt > -1) {
      value = saved.slice(0, bangAt - 1);   // strip "!important" + the '!'
      priority = 'important';
    } else {
      value = saved;
      priority = '';
    }
    if (value === '') {
      el.style.removeProperty(cssProp);
    } else {
      el.style.setProperty(cssProp, value, priority);
    }
    delete el.dataset[dsKey];
    delete el.dataset[dsKey + 'Val'];
  }
}

function restoreAll() {
  // Any element we've touched has at least one dm* dataset key
  const els = document.querySelectorAll(
    '[data-dm-bg], [data-dm-bg-img], [data-dm-color], [data-dm-fill], ' +
    '[data-dm-border], [data-dm-border-top], [data-dm-border-right], ' +
    '[data-dm-border-bottom], [data-dm-border-left]'
  );
  for (const el of els) restoreOne(el);
}

let observer = null;
let scheduled = false;
let pendingTargets = new Set();
let active = false;  // gate for pending rAF — if disabled fires between
                     // schedule() and flush(), the rAF must NOT re-apply

function flush() {
  scheduled = false;
  if (!active) { pendingTargets.clear(); return; }  // disabled mid-frame
  if (pendingTargets.size === 0) {
    applyAll(document.body);
  } else {
    for (const t of pendingTargets) {
      if (t && t.isConnected) applyAll(t);
    }
    pendingTargets.clear();
  }
}

function schedule(target) {
  if (!active) return;
  if (target) pendingTargets.add(target);
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(flush);
}

export function enableDarkOverrides() {
  active = true;
  applyAll(document.body);

  if (observer) observer.disconnect();
  observer = new MutationObserver((mutations) => {
    if (!active) return;
    for (const m of mutations) {
      if (m.type === 'attributes' && m.attributeName === 'style') {
        schedule(m.target);
      } else if (m.type === 'childList') {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1) schedule(node);
        }
      }
    }
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style'],
  });
}

export function disableDarkOverrides() {
  active = false;
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  pendingTargets.clear();
  restoreAll();
  // A second restore on the next frame catches anything React re-rendered
  // (and our now-stale MutationObserver missed) while we were toggling off.
  requestAnimationFrame(() => { if (!active) restoreAll(); });
}
